import React from "react";

import { FormattedMessage, useIntl } from "react-intl";

import { Link } from "ds/Button";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import {
  environmentSelector,
  environmentLoadingSelector
} from "Reducers/environment";
import { currentDeploymentErrorSelector } from "Reducers/environment/deployment";
import { useAppSelector } from "Store/hooks";

import * as S from "./NoServices.styles";

export interface NoServicesProps {
  children: React.ReactNode;
  organizationId: string;
  projectId: string;
  environmentId: string;
}

const NoServices = ({
  children,
  organizationId,
  projectId,
  environmentId
}: NoServicesProps) => {
  const intl = useIntl();

  const environment = useSelectorWithUrlParams(environmentSelector);
  const isEnvironmentLoading = useSelectorWithUrlParams(
    environmentLoadingSelector
  );
  const currentDeploymentError = useAppSelector(state =>
    currentDeploymentErrorSelector(state, {
      organizationId,
      projectId,
      environmentId
    })
  );

  let message: React.ReactNode;

  if (currentDeploymentError) {
    message = (
      <>
        <S.EmptyServicesTitle tag="h3">
          <FormattedMessage id="services.display.error.title" />
        </S.EmptyServicesTitle>
        <p>
          <FormattedMessage
            id="services.display.error.description"
            values={{
              a: (...chunks) => (
                <Link
                  analyticId="links.platform.contact"
                  href={intl.formatMessage({ id: "links.platform.contact" })}
                >
                  {chunks}
                </Link>
              ),
              br: () => <br />
            }}
          />
        </p>
      </>
    );
  }

  if (!isEnvironmentLoading && environment && !environment.has_code) {
    message = (
      <>
        <S.EmptyServicesTitle tag="h3">
          <FormattedMessage id="services.display.empty.title" />
        </S.EmptyServicesTitle>
        <p>
          <FormattedMessage
            id="services.display.empty.description"
            values={{
              code: (...chunks) => <S.Code variant="inline">{chunks}</S.Code>,
              a: (...chunks) => (
                <Link
                  href={intl.formatMessage({ id: "links.documentation" })}
                  analyticId="links.documentation"
                >
                  {chunks}
                </Link>
              ),
              br: () => <br />,
              appConfigFolder: intl.formatMessage({
                id: "configuration_folder"
              })
            }}
          />
        </p>
      </>
    );
  }

  if (message) {
    return (
      <S.Layout>
        <S.Title tag="h2">
          <FormattedMessage id="services" />
        </S.Title>
        <S.Tree maxMessageWidth="100%">{message}</S.Tree>
      </S.Layout>
    );
  }

  return <>{children}</>;
};

export default NoServices;
